import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
import { profilePlanRoute } from "@/routes/_app/profile.plan";
import { sandboxModalRoute } from "@/routes/_app/modal";
import { transactionsRoute } from "@/routes/_app/transactions";
import { accountsRoute } from "@/routes/_app/accounts";
import { categoriesRoute } from "@/routes/_app/categories";
import { reportsRoute } from "@/routes/_app/reports";
import { calendarRoute } from "@/routes/_app/calendar";
import { budgetsRoute } from "@/routes/_app/budgets";
import { recurringRoute } from "@/routes/_app/recurring";
import { projectsRoute } from "@/routes/_app/projects";
import { projectDetailRoute } from "@/routes/_app/projects.$projectId";
import { debtsRoute } from "@/routes/_app/debts";
import { pluginRoutes } from "@/composition";
import { trashRoute } from "@/routes/_app/trash";
import { dataRoute } from "@/routes/_app/data";
import { financeSettingsRoute } from "@/routes/_app/settings.finance";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    indexRoute, // path: "/" — Dashboard financiero
    transactionsRoute, // path: "/transactions"
    accountsRoute, // path: "/accounts"
    categoriesRoute, // path: "/categories"
    reportsRoute, // path: "/reports"
    calendarRoute, // path: "/calendar"
    budgetsRoute, // path: "/budgets"
    recurringRoute, // path: "/recurring"
    projectsRoute, // path: "/projects"
    projectDetailRoute, // path: "/projects/$projectId"
    debtsRoute, // path: "/debts"
    ...pluginRoutes, // path: "/activity" — ACTIVITY-CORE feed
    trashRoute, // path: "/trash" — papelera (L2)
    dataRoute, // path: "/data"
    financeSettingsRoute, // path: "/settings/finance"
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    profilePlanRoute, // path: "/profile/plan" — MONETIZATION_QUOTAS
    sandboxModalRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // Unknown URL → "not found" state with a CTA to a safe place, instead
  // of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
